import { useEffect } from 'react'
import {
  type ConsumerPotentialFragment,
  ConsumerPotentialsDocument,
  type PotentialCreateMutationVariables,
  PotentialSourceTypeEnum,
  useConsumerPotentialsQuery,
  usePotentialCreateMutation,
  usePotentialDeleteMutation,
} from '../generated/graphql'
import type { SearchProduct } from '../search/searchClient'
import { useAuthStore } from '../store/auth'
import { useCheckoutStore } from '../store/checkout'
import { useSelectionStore } from '../store/selection'
import { isOverCap } from './voucherCap'

/**
 * The consumer's "Potentials" on Core are the source of truth (no duplicated client store like
 * v1's Recoil cart). They split two ways:
 *   - order feed  — items auto-imported from past online orders (`sourceType: ONLINE`); these are
 *                   *candidates* the customer taps to trade in (only those with a real offer).
 *   - added       — items the customer added this session (search / photo); always in the basket.
 * A no-feed customer simply has an empty order feed.
 */

/**
 * Source types Core auto-imports from a customer's past purchases (online orders, receipts,
 * digital passes). These are trade-in *candidates* the customer selects — the "order feed" — NOT
 * things they added. Only SEARCH (catalogue) and WHOLESALE (manual) are user-initiated this
 * session and belong in the basket outright.
 */
const ORDER_FEED_SOURCES: PotentialSourceTypeEnum[] = [
  PotentialSourceTypeEnum.Online,
  PotentialSourceTypeEnum.Reciept,
  PotentialSourceTypeEnum.Digitalpass,
]

export function isOrderFeedSource(sourceType?: PotentialSourceTypeEnum | null): boolean {
  return sourceType != null && ORDER_FEED_SOURCES.includes(sourceType)
}

/**
 * Whether an order-feed item may actually be traded in. `offer.validForTradeIn` is the
 * authoritative eligibility flag (matches v1's ReturnsPage gate) — a value alone isn't enough:
 * e.g. a gift card can carry a point value but is `validForTradeIn: false`, so it's excluded.
 */
function isTradeableFeedItem(p: ConsumerPotentialFragment): boolean {
  return p.offer?.validForTradeIn === true
}

/** Raw split of the consumer's potentials into the order feed and manually-added items. */
export function useAllPotentials() {
  const { data, loading, error } = useConsumerPotentialsQuery({ fetchPolicy: 'cache-and-network' })
  const all = (data?.ConsumerPotentials ?? []).filter(
    (p): p is ConsumerPotentialFragment => Boolean(p),
  )
  const orderFeed = all.filter((p) => isOrderFeedSource(p.sourceType) && isTradeableFeedItem(p))
  const added = all.filter((p) => !isOrderFeedSource(p.sourceType))
  return { orderFeed, added, loading, error }
}

/** Past-orders feed + selection controls (order-feed customers). */
export function useOrderFeed() {
  const { orderFeed, loading } = useAllPotentials()
  const selected = useSelectionStore((s) => s.selected)
  const toggle = useSelectionStore((s) => s.toggle)
  return {
    orders: orderFeed,
    hasFeed: orderFeed.length > 0,
    isSelected: (id: string) => Boolean(selected[id]),
    toggle,
    loading,
  }
}

/**
 * The effective trade-in basket: every manually-added item plus the selected order-feed items.
 * The reward is derived from each potential's live offer.
 */
export function usePotentials() {
  const { added, orderFeed, loading, error } = useAllPotentials()
  const selected = useSelectionStore((s) => s.selected)
  const storeCurrency = useAuthStore((s) => s.config.currency)

  const potentials = [...added, ...orderFeed.filter((p) => selected[p._id])]
  const reward = potentials.reduce((sum, p) => sum + (p.offer?.value ?? 0), 0)
  // Display in the customer's preferred currency (from ConsumerLogin), not whatever currency the
  // individual offers happen to be tagged with — keeps the rail consistent with the rest of the UI.
  const currency = storeCurrency ?? potentials.find((p) => p.offer?.currency)?.offer?.currency ?? 'DKK'

  return { potentials, reward, currency, loading, error }
}

/** Whether the basket's reward exceeds the shop's configured voucher maximum. */
export function useVoucherCap() {
  const { reward } = usePotentials()
  const max = useAuthStore((s) => s.config.maximumVoucher) ?? 0
  return { max, exceeded: isOverCap(reward, max) }
}

/**
 * Keep the checkout stories in sync with the basket: when a product leaves the basket (removed via
 * the rail ✕ or deselected from the feed) its story is dropped, so a story never outlives its
 * product. Mount once in the shared layout so removal from anywhere takes the story with it.
 */
export function useSyncStoriesToBasket() {
  const { potentials } = usePotentials()
  const pruneStories = useCheckoutStore((s) => s.pruneStories)
  // Depend on the id set (stable string) rather than the array identity, which changes every render.
  const idKey = potentials.map((p) => p._id).join(',')
  useEffect(() => {
    pruneStories(idKey ? idKey.split(',') : [])
  }, [idKey, pruneStories])
}

/**
 * PotentialCreate variables for a catalogue match (text search or visual search — both produce the
 * same `SearchProduct`). Beyond the identity (`masterVariantId` + the authoritative `ruleId`):
 *
 *   - `imageUrl` — a visual-search match's `masterVariantId` doesn't resolve to a variant image
 *     server-side, so without this the created potential (and the trade-in item on "My trade-ins")
 *     would have no picture. Text matches carry the same catalogue image, so it's consistent for both.
 *   - `currency` — the currency `tradeinsearch2` priced the match in. Core prices the potential from
 *     that currency's RRP, so a 150 DKK match becomes a 150 DKK offer rather than being re-derived
 *     from another currency's RRP. Core stays authoritative for the final figure; we only tell it
 *     which currency the customer was shown.
 *
 * Both are guarded against the endpoint's possible empty strings — an omitted argument leaves Core
 * on its existing fallbacks, an empty one would not.
 */
export function catalogueItemVariables(
  consumerId: string,
  product: SearchProduct,
): PotentialCreateMutationVariables {
  return {
    consumerId,
    masterVariantId: product.masterVariantId,
    ruleId: product.ruleId,
    imageUrl: product.image || undefined,
    currency: product.currency || undefined,
  }
}

/** Add a catalogue search result to the basket via PotentialCreate. */
export function useAddCatalogueItem() {
  const consumerId = useAuthStore((s) => s.consumerId)
  const [create, { loading }] = usePotentialCreateMutation({
    refetchQueries: [{ query: ConsumerPotentialsDocument }],
    awaitRefetchQueries: true,
  })

  const add = (product: SearchProduct) => {
    if (!consumerId) return Promise.resolve()
    return create({ variables: catalogueItemVariables(consumerId, product) })
  }

  return { add, adding: loading }
}

/**
 * PotentialCreate variables for the "add by category" fallback. No `currency`: a category item is
 * priced from the category's own flat value, not from a catalogue RRP, so there is no search
 * currency to preserve. The photo the customer took is attached via `imageUrl` (the hosted
 * photo-receiver URL — Core accepts a URL directly, so no separate image upload/id step is needed).
 */
export function categoryItemVariables(
  consumerId: string,
  categoryId: string,
  imageUrl?: string | null,
): PotentialCreateMutationVariables {
  return { consumerId, categoryId, imageUrl: imageUrl ?? undefined }
}

/** Add an item by whole-sale category (the "add by category" fallback). */
export function useAddCategoryItem() {
  const consumerId = useAuthStore((s) => s.consumerId)
  const [create, { loading }] = usePotentialCreateMutation({
    refetchQueries: [{ query: ConsumerPotentialsDocument }],
    awaitRefetchQueries: true,
  })

  const add = (categoryId: string, imageUrl?: string | null) => {
    if (!consumerId) return Promise.resolve()
    return create({ variables: categoryItemVariables(consumerId, categoryId, imageUrl) })
  }

  return { add, adding: loading }
}

/** Remove a potential from the basket. The basket ✕ is the single, consistent remove control. */
export function useRemovePotential() {
  const [del, { loading }] = usePotentialDeleteMutation({
    refetchQueries: [{ query: ConsumerPotentialsDocument }],
    awaitRefetchQueries: true,
  })

  const remove = (potentialId: string) => del({ variables: { potentialId } })

  return { remove, removing: loading }
}
